import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-5">
      <div className="grain" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        <Link to="/" data-testid="login-back" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A] mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-8 sm:p-10">
          <span className="w-12 h-12 rounded-full bg-[#BE923C]/10 flex items-center justify-center text-[#BE923C]">
            <Lock size={22} />
          </span>
          <h1 className="mt-5 font-serif text-3xl tracking-tight">Admin Login</h1>
          <p className="mt-2 text-sm text-[#475569]">Manage events, RSVPs, gallery, and messages.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5 border-[#0F172A]/10" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-full bg-[#0F172A] text-white font-semibold py-3.5 hover:bg-[#1e293b] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
